<template>
  <div class="container" :class="{ spinner: isBusy }">
    <b-modal
      ref="modalRuleCheckFailed"
      :title="$t('Webportal.Dialog.RuleCheckFailed.Title')"
      :ok-title="$t('Webportal.Dialog.UnsavedChanges.Ok')"
      ok-variant="danger"
      :cancel-title="$t('Webportal.Dialog.UnsavedChanges.Cancel')"
      no-close-on-esc
      no-close-on-backdrop
      hide-header-close
    >
      {{ $t("Webportal.Dialog.RuleCheckFailed.Text") }}
    </b-modal>

    <h5>{{ $t('Webportal.Kpi') }}</h5>
    <div class="form-group form-row">
      <div class="col">
        <treeselect ref="kpiTreeselect"
                    :value="kpiValue"
                    :auto-load-root-options="false"
                    :placeholder="$t('Webportal.KpiFilter.Placeholder')"
                    :options="kpiItems"
                    valueFormat="object"
                    :disabled="isDisabled || surveyId == null"
                    :disableFuzzyMatching="true"
                    :multiple="false"
                    :showCount="true"
                    showCountOf="LEAF_DESCENDANTS"
                    :defaultExpandLevel="1"
                    :disable-branch-nodes="true"
                    @input="onKpiValueInput">
          <template #option-label="{ node, shouldShowCount, count, countClassName }">
            <div v-if="node.isBranch">{{ node.label }} <span v-if="shouldShowCount" :class="countClassName">({{ count }})</span></div>
            <div v-else>
              {{ node.label }}
              <b-badge v-if="node.raw.isCustomAssigned" variant="warning"
                v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.KpiQuestionAssignment.CustomAssignment.Description')">
                <font-awesome-icon :icon="['far', 'exclamation-circle']" /> {{ $t('Webportal.KpiQuestionAssignment.CustomAssignment') }}
              </b-badge>
            </div>
          </template>
        </treeselect>
      </div>
      <div class="col-auto">
        <b-form-radio-group buttons button-variant="outline-secondary"
                            v-model="currentKpiQuestionAssignment.HasCustomAssignment"
                            :disabled="isDisabled || kpiValue == null">
          <b-form-radio :value="false">{{ $t('Webportal.KpiQuestionAssignment.Default') }}</b-form-radio>
          <b-form-radio :value="true">{{ $t('Webportal.KpiQuestionAssignment.CustomAssignment') }}</b-form-radio>
        </b-form-radio-group>
      </div>
    </div>

    <div v-show="kpiValue" class="form-group card">
      <div class="card-header">
        <h5>{{ $t('Webportal.KpiQuestionAssignment.AssignedQuestions') }}</h5>
        <div v-if="hasKpiCustomAssignment" class="form-row">
          <div class="col">
            <treeselect v-model="questionValue"
                        :auto-load-root-options="false"
                        :placeholder="$t('Webportal.QuestionFilter.Placeholder')"
                        :options="questionItems"
                        :disabled="isDisabled || surveyId == null"
                        :disableFuzzyMatching="true"
                        :multiple="false"
                        :showCount="true"
                        showCountOf="LEAF_DESCENDANTS"
                        :defaultExpandLevel="1"
                        :disable-branch-nodes="true">
            </treeselect>
          </div>
          <div class="col-auto">
            <b-button variant="secondary" @click="addKpiQuestion" :disabled="isDisabled || questionValue == null">
              <font-awesome-icon :icon="['fas', 'plus']" /> {{ $t('Webportal.KpiQuestionAssignment.AddQuestion') }}
            </b-button>
          </div>
        </div>
      </div>
      <div class="list-group list-group-flush border-top-0 overflow-auto assigned-questions-card">
        <div 
          v-for="item in currentKpiQuestionList" :key="item.KpiQuestion.SurveyQuestionId"
          class="list-group-item list-group-item-action"
          :class="{ 'active': item.KpiQuestion == selectedKpiQuestion }"
          role="button"
          @click.stop="onKpiQuestionClick(item.KpiQuestion)">

          <div class="form-row mr-0 align-items-center">
            <div class="col">
              {{ item.Question ? item.Question.QuestionText : "Unknown question" }}
            </div>
            <template v-if="hasKpiCustomAssignment" >
              <div class="col-auto">
                <b-form-radio-group buttons button-variant="outline-secondary" size="sm"
                                    v-model="item.KpiQuestion.IsRuleBased"
                                    :disabled="isDisabled">
                  <b-form-radio :value="false"><font-awesome-icon :icon="['fas', 'eye']" /> {{ $t('Webportal.KpiQuestionAssignment.AlwaysVisible') }}</b-form-radio>
                  <b-form-radio :value="true"><font-awesome-icon :icon="['fas', 'function']" /> {{ $t('Webportal.KpiQuestionAssignment.RuleBased') }}</b-form-radio>
                </b-form-radio-group>
              </div>
              <div class="col-auto">
                <b-button variant="outline-secondary" size="sm" @click.stop="deleteKpiQuestion(item.KpiQuestion)" :disabled="isDisabled">
                  <font-awesome-icon :icon="['fas', 'times']" />
                </b-button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div v-show="hasKpiCustomAssignment">
      <div class="form-group">
        <rule-builder
          ref="ruleBuilder"
          v-model="currentKpiQuestionRules"
          :surveyId="surveyId"
          @update:formula="onRuleFormulaUpdate"
          :disabled="isRuleBuilderDisabled" />
      </div>

      <div class="form-group">
        <b-button :pressed.sync="showRulesData" variant="secondary" size="sm" :disabled="isDisabled">
          <font-awesome-icon :icon="['fas', 'vial']" /> {{ showRulesData ? $t('Webportal.KpiQuestionAssignment.HideRuleDataEditor') : $t('Webportal.KpiQuestionAssignment.ShowRuleDataEditor') }} 
        </b-button>
      </div>

      <div v-if="showRulesData" class="form-group">
        <textarea
          v-model="currentKpiQuestionRulesJSON"
          class="form-control"
          :style="{ fontFamily: 'Courier' }"
          :disabled="isRuleBuilderDisabled"
          autocomplete="false"
          spellcheck="false"
          rows="10"
          @blur="currentKpiQuestionRulesJSON = currentKpiQuestionRulesJSON">
        </textarea>
      </div>
    </div>

    <div class="float-right">
      <b-button variant="primary" @click="applyData" :disabled="isBusy || !hasChanged || !isValid">
        <font-awesome-icon :icon="['fas', 'share-all']" /> {{ $t('Webportal.KpiQuestionAssignment.ApplyData') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import EventBus from '@/services/EventBus.js';
import RuleBuilder from './RuleBuilder/RuleBuilder.vue';

import SurveysService from '@/services/SurveysService'

export default {
  name: "KpiQuestionAssignment",

  mixins: [ AppConfig ],

  components: {
    RuleBuilder
  },

  props: {
    surveyId: { type: Number, default: null },
    disabled: { type: Boolean, default: false }
  },

  data() {
    return {
      isBusy: false,

      kpiData: null,
      kpiValue: null,

      questionData: null,
      questionValue: null,

      kpiAssignmentData: [],

      selectedKpiQuestion: null,

      changedKpiAssignmentData: [],

      showRulesData: false
    }
  },

  computed: {
    isDisabled() {
      return this.disabled;
    },

    hasChanged() {
      return this.changedKpiAssignmentData.length > 0;
    },

    isValid() {
      return !this.isKpiQuestionRuleBasedSelected || this.$refs.ruleBuilder.isValid;
    },

    isRuleBuilderDisabled() {
      return this.isDisabled || this.selectedKpiQuestion == null || !this.selectedKpiQuestion.IsRuleBased;
    },

    hasKpiCustomAssignment() {
      return this.currentKpiQuestionAssignment.HasCustomAssignment == true;
    },

    isKpiQuestionRuleBasedSelected() {
      return this.selectedKpiQuestion?.IsRuleBased;
    },

    kpiItems() {
      const fnMapKpi = kpi => (
        {
          id: kpi.id,
          label: kpi.label,
          ... kpi.children != null ? { children: kpi.children.map(fnMapKpi) } : null,
          ... kpi.id[0] == 'K' ? { isCustomAssigned: this.findKpiQuestionAssignment(kpi.id.substring(1))?.HasCustomAssignment == true } : null
        });

      return this.kpiData?.map(fnMapKpi) ?? [];
    },

    questionItems() {
      return this.questionData?.map(question => (
        {
          id: question.SurveyQuestionId,
          label: question.QuestionText,
          isDisabled: this.currentKpiQuestionAssignment.CustomQuestions?.find(q => q.SurveyQuestionId == question.SurveyQuestionId) != null
        })) ?? [];
    },

    selectedKpiId() {
      return this.kpiValue?.id.substring(1);
    },

    currentKpiQuestionAssignment() {
      let result = this.findKpiQuestionAssignment(this.selectedKpiId);

      if (result == null) {
        result = { SurveyKpiId: this.selectedKpiId, HasCustomAssignment: false, CustomQuestions: [] };
        this.kpiAssignmentData.push(result);
      }

      return result;
    },

    currentKpiQuestionList() {
      return _(this.hasKpiCustomAssignment ? this.currentKpiQuestionAssignment.CustomQuestions : this.currentKpiQuestionAssignment.Questions)
        .map(kq => ({
          KpiQuestion: kq,
          Question: this.findQuestion(kq.SurveyQuestionId)
        }))
        .orderBy([kq => kq.Question?.PositionInSurvey, kq => kq.Question?.PositionInSection])
        .value();
    },

    currentKpiQuestionRules: {
      get() {
        if (this.isKpiQuestionRuleBasedSelected)
        {
          this.$set(this.selectedKpiQuestion, "Rules", this.selectedKpiQuestion.Rules ?? {});
          return this.selectedKpiQuestion.Rules;
        }
        else
          return null;
      },
      set(newValue) {
        if (this.isKpiQuestionRuleBasedSelected)
          this.$set(this.selectedKpiQuestion, "Rules", newValue);
      }
    },

    currentKpiQuestionRulesJSON: {
      get() {
        return JSON.stringify(this.currentKpiQuestionRules, null, 2);
      },
      set(newValue) {
        try {
            this.currentKpiQuestionRules = JSON.parse(newValue);
        } catch (error) {
        }
      }
    },

    changedKpiAssignmentDataForSubmit() {
      return this.changedKpiAssignmentData.map(item => 
        _.pick(item, ['SurveyKpiId', 'HasCustomAssignment', 'CustomQuestions']));
    },
  },

  watch: {
    surveyId() {
      this.kpiValue = null;
      this.selectedKpiQuestion = null;
      this.changedKpiAssignmentData = [];

      this.loadData();
    },

    currentKpiQuestionAssignment: {
      handler(newVal, oldVal) {
        if (newVal === oldVal)
          if (newVal.SurveyKpiId && !this.changedKpiAssignmentData.includes(newVal))
            this.changedKpiAssignmentData.push(newVal);
      },
      deep: true
    },
  },

  mounted() {
    this.loadData();

    EventBus.$on('LocalizationChanged', this.localizationChanged);
  },

  beforeDestroy() {
    EventBus.$off('LocalizationChanged', this.localizationChanged);
  },

  methods: {
    localizationChanged(localizationId) {
      this.loadData();
    },

    loadData() {
      if (this.surveyId) {
        this.isBusy = true;

        Promise.all([
          SurveysService.getSurveyKpis({ surveyIds: [ this.surveyId ] }),
          SurveysService.getSurveyQuestions([this.surveyId]),
          SurveysService.getSurveyKpiAssignments(this.surveyId)
        ])
          .then(([responseKpis, responseQuestions, responseSurveyKpiAssignments]) => {
            if (responseKpis.data && responseKpis.data.length === 1 && responseKpis.data[0].children)
              this.kpiData = responseKpis.data[0].children;

            if (responseQuestions.data && responseQuestions.data.length === 1 && responseQuestions.data[0].Questions)
              this.questionData = responseQuestions.data[0].Questions;

            this.kpiAssignmentData = responseSurveyKpiAssignments.data ?? [];
          })
          .finally(_ => {
            this.isBusy = false;
          });
      } else {
        this.kpiData = [];
        this.questionData = [];
      }
    },

    findKpiQuestionAssignment(kpiId) {
      return this.kpiAssignmentData.find(kqd => kqd.SurveyKpiId == kpiId);
    },

    findQuestion(surveyQuestionId) {
      return this.questionData?.find(q => q.SurveyQuestionId == surveyQuestionId);
    },

    ruleCheck(callback) {
      if (this.hasKpiCustomAssignment && this.isKpiQuestionRuleBasedSelected && !this.$refs.ruleBuilder.isValid) {
        let dialog = this.$refs.modalRuleCheckFailed;

        dialog.$off();
        dialog.$on("ok", _ => callback(true));
        dialog.$on("cancel", _ => callback(false));
        dialog.show();
      } else {
        callback();
      }
    },

    addKpiQuestion() {
      this.ruleCheck(discard => {
        if (discard || discard == null) {
          if (discard)
            this.selectedKpiQuestion.IsRuleBased = false;

          let kpiQuestion = { SurveyQuestionId: this.questionValue, IsRuleBased: false };
          if (this.currentKpiQuestionAssignment.CustomQuestions == null)
            this.$set(this.currentKpiQuestionAssignment, "CustomQuestions", []);
          this.currentKpiQuestionAssignment.CustomQuestions.push(kpiQuestion);
          this.selectedKpiQuestion = kpiQuestion;
          this.questionValue = null;
        }
      });
    },

    deleteKpiQuestion(kpiQuestion) {
      let index = this.currentKpiQuestionAssignment.CustomQuestions.findIndex(kq => kq.SurveyQuestionId == kpiQuestion.SurveyQuestionId);

      if (index > -1)
      {
        if (this.selectedKpiQuestion === kpiQuestion)
          this.selectedKpiQuestion = null;
        this.currentKpiQuestionAssignment.CustomQuestions.splice(index, 1);
      }
    },

    onKpiValueInput(value) {
      if (this.kpiValue !== value) {
        let currentValue = this.kpiValue;

        this.ruleCheck(discard => {
          if (discard || discard == null) {
            if (discard)
              this.selectedKpiQuestion.IsRuleBased = false;
            this.selectedKpiQuestion = null;
            this.kpiValue = value;
          } else {
            this.kpiValue = value
            setTimeout(() => {
              this.kpiValue = currentValue;  
            }, 0);
          }
        });
      }
    },

    onKpiQuestionClick(kpiQuestion) {
      if (this.selectedKpiQuestion !== kpiQuestion)
        this.ruleCheck(discard => {
          if (discard || discard == null) {
            if (discard)
              this.selectedKpiQuestion.IsRuleBased = false;
            this.selectedKpiQuestion = kpiQuestion;
          }
        });
    },

    onRuleFormulaUpdate(formula) {
      if (this.selectedKpiQuestion != null && this.selectedKpiQuestion.RuleFormula != formula)
        this.selectedKpiQuestion.RuleFormula = formula;
    },

    async applyData() {
      this.isBusy = true;

      try {
        await SurveysService.setSurveyKpiCustomAssignments(
          this.surveyId,
          this.changedKpiAssignmentDataForSubmit);

        this.$bvToast.toast(
          this.$t("Webportal.SaveSuccess.Text", {
            entity: this.$t("Webportal.KpiQuestionAssignments"),
          }),
          {
            autoHideDelay: 2000,
            variant: "success",
            noCloseButton: true,
          }
        );

        this.changedKpiAssignmentData = [];
      } catch (error) {
        console.error(error);

        this.$bvToast.toast(
          this.$t("Webportal.SaveFailed.Text", {
            entity: this.$t("Webportal.KpiQuestionAssignments"),
          }),
          {
            autoHideDelay: 10 * 1000,
            variant: "danger",
            noCloseButton: true,
          }
        );
        throw error;
      } finally {
        this.isBusy = false;
      }
    }
  }
}
</script>

<style scoped>
  .assigned-questions-card {
    min-height: 56px;
    max-height: 280px;
  }
</style>